'use client';

import { useCallback } from 'react';
import { PublicKey } from '@velocity-exchange/sdk';
import { getUserKey } from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { notify } from 'src/utils/notifications';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import type { Adapter } from '@solana/wallet-adapter-base';
import invariant from 'tiny-invariant';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import useIsSolflareWalletConnected from 'src/hooks/useIsSolflareWalletConnected';

/**
 * Hook for updating the delegate authority of a subaccount.
 */
export const useUpdateSubaccountDelegate = () => {
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const walletAdapter = useVelocityStore((s) => s.wallet.current.adapter);
	const setVelocityAccountsStore = useVelocityAccountsStore((s) => s.set);
	const getTxParams = useGetTxParams();
	const isSolflareWalletConnected = useIsSolflareWalletConnected();

	const updateDelegateAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			accountId: number,
			delegate: string
		) => {
			invariant(velocityClient, 'driftClient is required');

			const ix = await velocityClient.getUpdateUserDelegateIx(
				new PublicKey(delegate),
				{ subAccountId: accountId }
			);

			await wallet.signAndSendTransaction(ix, getTxParams());
		},
		[velocityClient, getTxParams]
	);

	// there is an issue with Solflare not correctly signing the transaction when attempting to delegate with fee payer sponsorship
	const wrappedUpdateDelegate = useSponsoredSignVelocityActionWithFallback(
		updateDelegateAction,
		isSolflareWalletConnected
	);

	const updateSubaccountDelegate = useCallback(
		async (accountId: number, delegate: string): Promise<boolean> => {
			if (!velocityClient) return false;

			const toastId = `updatingSubaccountDelegate${accountId}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedUpdateDelegate(accountId, delegate);

				const isRemoval = delegate.toString() === DEFAULT_PUBLIC_KEY.toString();

				notify({
					id: toastId,
					type: 'success',
					message: `Subaccount delegate ${isRemoval ? 'removed' : 'updated'}.`,
					showUntilCancelled: false,
				});

				setVelocityAccountsStore((s) => {
					const userKey = getUserKey(
						accountId,
						velocityClient?.authority as PublicKey
					);
					if (s.accounts[userKey] !== undefined) {
						s.accounts[userKey].delegate = new PublicKey(delegate);
						s.accounts[userKey].isDelegatedAway = !isRemoval;
					}
				});

				return true;
			} catch (e: any) {
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter as Adapter,
				});
				return false;
			}
		},
		[
			velocityClient,
			walletAdapter,
			setVelocityAccountsStore,
			wrappedUpdateDelegate,
		]
	);

	return { updateSubaccountDelegate };
};
